import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCvAccessGrant,
  createCvShareLink,
  ensureCvPrivateLink,
  ensureCvPublicLink,
  fetchCvPrivateLink,
  fetchCvAccessGrants,
  fetchCvActivity,
  fetchCvLanguageVariants,
  fetchReceivedCvAccessGrants,
  fetchCvShareLinks,
  generateCvLanguageVariant,
  revokeCvAccessGrant,
  updateCvAccessGrant,
  updateCvShareLink,
  type CvTargetKey,
} from '../lib/cv-collaboration-api';

const variantsKey = (target: CvTargetKey) => ['cv', target, 'variants'] as const;
const accessKey = (target: CvTargetKey) => ['cv', target, 'access-grants'] as const;
const receivedAccessKey = ['cv', 'access-grants', 'received'] as const;
const shareKey = (target: CvTargetKey) => ['cv', target, 'share-links'] as const;
const publicLinkKey = (target: CvTargetKey) => ['cv', target, 'public-link'] as const;
const privateLinkKey = (target: CvTargetKey) => ['cv', target, 'private-link'] as const;
const activityKey = (target: CvTargetKey) => ['cv', target, 'activity'] as const;

export function useCvLanguageVariants(target: CvTargetKey) {
  return useQuery({
    queryKey: variantsKey(target),
    queryFn: () => fetchCvLanguageVariants(target),
    enabled: Boolean(target),
  });
}

export function useGenerateCvLanguageVariant(target: CvTargetKey) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      locale: string;
      direction?: 'ltr' | 'rtl';
      status?: 'draft' | 'published';
    }) => generateCvLanguageVariant(target, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: variantsKey(target) });
      queryClient.invalidateQueries({ queryKey: activityKey(target) });
    },
  });
}

export function useCvAccessGrants(target: CvTargetKey) {
  return useQuery({
    queryKey: accessKey(target),
    queryFn: () => fetchCvAccessGrants(target),
    enabled: Boolean(target),
  });
}

export function useReceivedCvAccessGrants(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: receivedAccessKey,
    queryFn: fetchReceivedCvAccessGrants,
    staleTime: 15_000,
    enabled: options.enabled ?? true,
  });
}

export function useCreateCvAccessGrant(target: CvTargetKey) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { granteeEmail: string; role: 'viewer' | 'editor' | 'admin' }) =>
      createCvAccessGrant(target, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessKey(target) });
      queryClient.invalidateQueries({ queryKey: receivedAccessKey });
      queryClient.invalidateQueries({ queryKey: activityKey(target) });
    },
  });
}

export function useUpdateCvAccessGrant(target: CvTargetKey) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      grantId: string;
      role?: 'viewer' | 'editor' | 'admin';
      permissions?: string[];
      status?: 'pending' | 'active' | 'revoked';
      expiresAt?: string | null;
    }) =>
      updateCvAccessGrant(target, input.grantId, {
        role: input.role,
        permissions: input.permissions,
        status: input.status,
        expiresAt: input.expiresAt,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessKey(target) });
      queryClient.invalidateQueries({ queryKey: receivedAccessKey });
      queryClient.invalidateQueries({ queryKey: activityKey(target) });
    },
  });
}

export function useRevokeCvAccessGrant(target: CvTargetKey) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) => revokeCvAccessGrant(target, grantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessKey(target) });
      queryClient.invalidateQueries({ queryKey: receivedAccessKey });
      queryClient.invalidateQueries({ queryKey: activityKey(target) });
    },
  });
}

export function useCvShareLinks(target: CvTargetKey) {
  return useQuery({
    queryKey: shareKey(target),
    queryFn: () => fetchCvShareLinks(target),
    enabled: Boolean(target),
  });
}

export function useCreateCvShareLink(target: CvTargetKey) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      variantId?: string | null;
      releaseMode?: "follow_approved" | "exact";
      releaseId?: string;
      label?: string | null;
      permissions?: string[];
      visibility?: Record<string, unknown>;
      allowDownload?: boolean;
      revokeExisting?: boolean;
    }) => createCvShareLink(target, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shareKey(target) });
      queryClient.invalidateQueries({ queryKey: activityKey(target) });
    },
  });
}

export function useEnsureCvPrivateLink(target: CvTargetKey) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      permissions?: string[];
      visibility?: Record<string, unknown>;
      allowDownload?: boolean;
      rotate?: boolean;
    }) => ensureCvPrivateLink(target, input),
    onSuccess: (result) => {
      // The next open of the Share sheet shows this link straight away.
      queryClient.setQueryData(privateLinkKey(target), result);
      queryClient.invalidateQueries({ queryKey: shareKey(target) });
      queryClient.invalidateQueries({ queryKey: activityKey(target) });
    },
  });
}

/** The live standing private link (null when none). Never creates one. */
export function useCvPrivateLink(
  target: CvTargetKey,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: privateLinkKey(target),
    queryFn: () => fetchCvPrivateLink(target),
    enabled: (options.enabled ?? true) && Boolean(target),
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 1,
  });
}

export function useCvPublicLink(
  target: CvTargetKey,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: publicLinkKey(target),
    queryFn: () => ensureCvPublicLink(target),
    enabled: (options.enabled ?? true) && target !== 'master',
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 1,
  });
}

export function useUpdateCvShareLink(target: CvTargetKey) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      shareLinkId: string;
      visibility?: Record<string, unknown>;
      allowDownload?: boolean;
      revoked?: boolean;
    }) =>
      updateCvShareLink(target, input.shareLinkId, {
        visibility: input.visibility,
        allowDownload: input.allowDownload,
        revoked: input.revoked,
      }),
    onSuccess: (_link, input) => {
      // The revoked link may be the standing one (or any other); re-read it
      // rather than guess, so a closed-and-reopened sheet never shows a dead URL.
      if (input.revoked) {
        queryClient.invalidateQueries({ queryKey: privateLinkKey(target) });
      }
      queryClient.invalidateQueries({ queryKey: shareKey(target) });
      queryClient.invalidateQueries({ queryKey: activityKey(target) });
    },
  });
}

export function useCvActivity(target: CvTargetKey) {
  return useQuery({
    queryKey: activityKey(target),
    queryFn: () => fetchCvActivity(target),
    enabled: Boolean(target),
  });
}
